import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { PageHero } from "@/components/page-parts";
import {
  CONTACT_NUMBERS,
  FIRM,
  FIRM_ADDRESS,
  FIRM_POSTAL_ADDRESS,
  PENINSULA_ADDRESS,
  PENINSULA_OFFICE,
  SERVICE_AREAS,
  localBusinessSchema,
  peninsulaBusinessSchema,
} from "@/lib/firm";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: fitTitle(`Contact Coastside Law — Golden Beach & Scarborough | ${FIRM.name}`) },
      {
        name: "description",
        content: fitDescription(`Coastside Law: ${FIRM_ADDRESS} and ${PENINSULA_ADDRESS}. Call ${FIRM.phone} for a fixed-fee conveyancing, wills or elder law quote.`),
      },
      { property: "og:title", content: fitTitle(`Contact | ${FIRM.name}`) },
      {
        property: "og:description",
        content: fitDescription(`Call ${FIRM.phone} or send an enquiry. Offices at Golden Beach, Caloundra and Scarborough, Redcliffe Peninsula.`),
      },
      { property: "og:url", content: "https://coastsidelaw.com.au/contact" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/contact" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusinessSchema) },
      { type: "application/ld+json", children: JSON.stringify(peninsulaBusinessSchema) },
    ],
  }),

  component: ContactPage,
});

function ContactPage() {
  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Get in touch"
        lead="Tell us what you need and we will come back to you with a fixed fee and clear next steps. If it is urgent, call — you will get a lawyer, not a queue."
      />

      <section className="mx-auto grid max-w-6xl gap-12 px-5 py-16 lg:grid-cols-[1fr_1.3fr]">
        <div>
          <dl className="space-y-7 text-sm">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-widest text-foreground">
                Phone
              </dt>
              <dd className="mt-2 space-y-3">
                {CONTACT_NUMBERS.map((c) => (
                  <div key={c.phone}>
                    <a href={c.phoneHref} className="font-serif text-2xl text-primary underline">
                      {c.phone}
                    </a>
                    <p className="text-xs text-muted-foreground">
                      {c.name} — {c.office}
                    </p>
                  </div>
                ))}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-widest text-foreground">
                Email
              </dt>
              <dd className="mt-2">
                <a href={FIRM.emailHref} className="text-primary underline">
                  {FIRM.email}
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-widest text-foreground">
                Offices
              </dt>
              <dd className="mt-2 space-y-4 text-muted-foreground">
                <div>
                  <p className="font-semibold text-foreground">Sunshine Coast</p>
                  {FIRM.street}
                  <br />
                  {FIRM.suburb} {FIRM.state} {FIRM.postcode}
                  <br />
                  <span className="text-xs">Appointments preferred</span>
                </div>
                <div>
                  <p className="font-semibold text-foreground">Redcliffe Peninsula</p>
                  {PENINSULA_OFFICE.street}
                  <br />
                  {PENINSULA_OFFICE.suburb} {PENINSULA_OFFICE.state} {PENINSULA_OFFICE.postcode}
                  <br />
                  <span className="text-xs">{PENINSULA_OFFICE.note}</span>
                </div>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-widest text-foreground">
                Postal address
              </dt>
              <dd className="mt-2 text-muted-foreground">{FIRM_POSTAL_ADDRESS}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-widest text-foreground">
                Hours
              </dt>
              <dd className="mt-2 text-muted-foreground">{FIRM.hours}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-widest text-foreground">
                Areas we serve
              </dt>
              <dd className="mt-2 text-muted-foreground">{SERVICE_AREAS.join(", ")}.</dd>
            </div>
          </dl>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={localBusinessSchema.hasMap}
              target="_blank"
              rel="noreferrer"
              className="inline-block rounded-sm border border-border bg-card px-4 py-3 text-sm font-semibold text-primary underline"
            >
              Directions — Theresa Street, Golden Beach
            </a>
            <a
              href={peninsulaBusinessSchema.hasMap}
              target="_blank"
              rel="noreferrer"
              className="inline-block rounded-sm border border-border bg-card px-4 py-3 text-sm font-semibold text-primary underline"
            >
              Directions — Fortune Street, Scarborough
            </a>
          </div>

          <p className="mt-8 rounded-sm border-l-2 border-accent bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {FIRM.membership}. Liability limited by a scheme approved under professional standards
            legislation.
          </p>
        </div>

        <EnquiryForm heading="Send us an enquiry" />
      </section>
      <div className="h-8" />
    </>
  );
}
